/**
 * usePlateLayout - where a person plate's callouts stand and the frame round them, for the cell it is drawn in
 * (plan plate-unboxed; engine/plate/calloutPlacement's free-spot search through calloutLayout's `personLayout`).
 *
 * The svg fills its cell, so its own box is the cell: a ResizeObserver on it gives the cell's size, in steps of 8 px
 * so a resize that changes nothing on screen re-renders nothing, and the placement holds the words to one size on
 * screen in it. The callouts are worked out here once, for the layout and for the plate's body alike. Without a
 * ResizeObserver (a test's DOM), the words stay their own size.
 *
 * @returns the callouts, the layout (the frame, each callout's column and row, the words' scale) and the cell's size
 *   (null until known): a kill-team brother's lane is placed in it (usePlateLane)
 */
import { useEffect, useMemo, useState, type RefObject } from 'react';
import { getBodyConfig } from '../../engine/bodyConfig';
import type { PlateArt } from '../../engine/plate/figureArt';
import { plateCallouts, type PlateCallout } from '../../engine/plate/plateModel';
import { PERSON_UNPLACED, personLayout } from '../../engine/plate/calloutLayout';
import type { Target } from '../../types';

const NO_CALLOUTS: PlateCallout[] = [];

export default function usePlateLayout(
  ref: RefObject<SVGSVGElement | null>,
  art: PlateArt | null,
  target: Target,
  { fill, status, words }: { fill: boolean; status: boolean; words: number },
) {
  const cell = useCellSize(ref, fill);
  const config = getBodyConfig(target.bodyType || 'humanoid');
  const callouts = useMemo(() => (art ? plateCallouts(art, target, config) : NO_CALLOUTS), [art, target, config]);
  const layout = useMemo(
    () => (art ? personLayout(art, callouts, { cell, status, words }) : PERSON_UNPLACED),
    [art, callouts, cell, status, words],
  );
  return { callouts, layout, cell };
}

/** A cell's size to the nearest 8 px: fine enough for the words, coarse enough that a drag does not re-place. */
const STEP = 8;
const step = (px: number) => Math.round(px / STEP) * STEP;

/**
 * The size of the svg's cell, or null until it has one, with no observer, or for a plate sized by its width alone
 * (it takes its height from the very viewBox this would choose, so it would answer itself - the Track P review). A
 * vehicle plate asks it too.
 */
export function useCellSize(ref: RefObject<SVGSVGElement | null>, fills: boolean): { w: number; h: number } | null {
  const [cell, setCell] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    const el = ref.current;
    // Read off globalThis, as useShellRail does: jsdom has none, and a bare name trips no-undef in a .ts module.
    const Observer = globalThis.ResizeObserver;
    if (!el || !Observer || !fills) {
      setCell(null);
      return undefined;
    }
    const watch = new Observer(([entry]) => {
      const next = { w: step(entry.contentRect.width), h: step(entry.contentRect.height) };
      setCell((was) => (was && was.w === next.w && was.h === next.h ? was : next));
    });
    watch.observe(el);
    return () => watch.disconnect();
  }, [ref, fills]);
  return cell;
}
